/**
 * Template Parser
 *
 * 通用的模板字符串和 HTML 字符串解析工具
 *
 * 支持两种字符串格式：
 * 1. 模板字符串格式：{{ expression }}
 * 2. HTML 字符串格式：<tag>...</tag>
 */
import React from 'react';
import { isFieldReaction } from './reaction-engine';

/**
 * 检查字符串是否是模板字符串格式（包含 {{ }}）
 */
export function isTemplateString(str: string): boolean {
  return typeof str === 'string' && /^\{\{(.+)\}\}$/.test(str);
}

/**
 * 检查字符串是否是 HTML 格式（包含 <xxx>...</xxx> 标签）
 * 支持格式：
 * - <tag>content</tag>
 * - <tag attr="value">content</tag>
 * - <tag/> 或 <tag />
 */
export function isHtmlString(str: string): boolean {
  if (typeof str !== 'string') {
    return false;
  }
  // 检测是否包含 HTML 标签
  // 匹配开始标签 <tag> 或 <tag attr="value"> 或自闭合标签 <tag/> 或 <tag />
  // 然后可能匹配结束标签 </tag>
  const htmlTagPattern = /<[a-zA-Z][a-zA-Z0-9]*(?:\s+[^>]*)?(?:\/>|>)/;
  return htmlTagPattern.test(str);
}

/**
 * 将 HTML 字符串解析为 ReactNode
 * 使用 dangerouslySetInnerHTML 渲染 HTML 内容
 */
export function parseHtmlString(htmlStr: string): React.ReactNode {
  if (!htmlStr || typeof htmlStr !== 'string') {
    return null;
  }

  // 创建一个包装元素来渲染 HTML
  return React.createElement('span', {
    dangerouslySetInnerHTML: { __html: htmlStr },
  });
}

/**
 * 解析模板字符串（统一处理函数和组件）
 * 支持格式：{{ expression }}
 *
 * @param templateStr 模板字符串
 * @param registry 注册表，包含所有可用的函数、组件和常量
 *
 * @example
 * // 函数表达式
 * parseTemplateString('{{ (fieldKey, fieldValue) => customFields2Filter(\'a12\', fieldValue) }}'
 * , { customFields2Filter })
 *
 * // 组件表达式
 * parseTemplateString('{{ iconTextHtml(\'<span>提示内容</span>\') }}', { iconTextHtml })
 *
 * @returns 解析后的函数或 ReactNode，如果解析失败返回 null
 */
export function parseTemplateString(
  templateStr: string,
  registry: Record<string, any> = {},
): any {
  if (!templateStr || typeof templateStr !== 'string') {
    return null;
  }

  // 检查是否是模板字符串格式（被 {{ }} 包裹）
  const templateMatch = templateStr.match(/^\{\{(.+)\}\}$/);
  if (!templateMatch) {
    return null;
  }

  try {
    const code = templateMatch[1].trim();

    // 使用 Function 构造函数安全执行，并将注册表中的所有内容注入到作用域
    // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func
    const func = new Function(
      ...Object.keys(registry),
      `return ${code}`,
    );

    return func(...Object.values(registry));
  } catch (e) {
    console.error('Failed to parse template string:', templateStr, e);
    return null;
  }
}

/**
 * 递归解析对象/数组中的所有模板字符串
 * 遍历整个数据结构，查找所有包含 {{}} 格式的字符串值并解析它们
 *
 * @param value 要处理的值（可以是对象、数组、字符串等）
 * @param registry 注册表，包含所有可用的函数、组件和常量
 * @param visited 用于防止循环引用的 Set（内部使用）
 * @param skipReactions 是否跳过 reactions 配置的解析（默认 true）
 * @param isFieldReaction 可选的函数，用于判断是否是 FieldReaction 对象
 * @returns 处理后的值
 */
export function parseTemplateStringsRecursively(
  value: any,
  registry: Record<string, any> = {},
  visited: WeakSet<any> = new WeakSet(),
  skipReactions = true,
): any {
  // 处理 null 和 undefined
  if (value === null || value === undefined) {
    return value;
  }

  // 处理字符串：检查是否是模板字符串格式或 HTML 格式
  if (typeof value === 'string') {
    // 优先检查模板字符串（{{ }} 格式）
    if (isTemplateString(value)) {
      const parsed = parseTemplateString(value, registry);
      // 如果解析成功，返回解析后的值；否则返回原值
      return parsed !== null ? parsed : value;
    }
    // 检查 HTML 字符串（<xxx>...</xxx> 格式）
    if (isHtmlString(value)) {
      const parsed = parseHtmlString(value);
      // 如果解析成功，返回解析后的 ReactNode；否则返回原值
      return parsed !== null ? parsed : value;
    }
    return value;
  }

  // 处理数组：递归处理每个元素
  if (Array.isArray(value)) {
    // 如果 skipReactions 为 true，检查是否是 reactions 数组（FieldReaction[]）
    if (skipReactions && isFieldReaction && value.length > 0 && isFieldReaction(value[0])) {
      // 这是 reactions 数组，跳过解析，直接返回原值
      // reactions 中的表达式字符串需要在运行时解析
      return value;
    }
    return value.map(item => parseTemplateStringsRecursively(
      item,
      registry,
      visited,
      skipReactions,
    ));
  }

  // 处理对象：递归处理每个属性
  if (typeof value === 'object') {
    // 防止循环引用
    if (visited.has(value)) {
      return value;
    }
    visited.add(value);

    // 如果 skipReactions 为 true，检查是否是 FieldReaction 对象
    if (skipReactions && isFieldReaction && isFieldReaction(value)) {
      // 这是 FieldReaction，跳过解析，直接返回原值
      // reactions 中的表达式字符串需要在运行时解析
      return value;
    }

    const result: any = {};
    Object.keys(value).forEach((key) => {
      // 如果 key 是 'reactions'，跳过解析（reactions 中的表达式需要在运行时解析）
      if (skipReactions && key === 'reactions') {
        result[key] = value[key];
      } else {
        result[key] = parseTemplateStringsRecursively(
          value[key],
          registry,
          visited,
          skipReactions,
        );
      }
    });
    return result;
  }

  // 其他类型（number, boolean, function 等）直接返回
  return value;
}

/**
 * 判断是否是普通对象（可用于深度合并）
 * 排除数组、null、Date、RegExp 等特殊对象
 */
export function isPlainObject(value: any): boolean {
  return (
    value
    && typeof value === 'object'
    && !Array.isArray(value)
    && !(value instanceof Date)
    && !(value instanceof RegExp)
  );
}

/**
 * 深度合并对象
 * 递归合并对象属性，数组和原始值会被覆盖
 */
export function deepMerge(target: any, source: any): any {
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return source;
  }
  if (!target || typeof target !== 'object' || Array.isArray(target)) {
    return source;
  }

  const result = { ...target };
  Object.keys(source).forEach((key) => {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = deepMerge(result[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  });
  return result;
}

